import { useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";

import FeedbackButton from "@/components/FeedbackButton";
import HelpButton from "@/components/HelpButton";
import RequireAuth from "@/components/RequireAuth";
import Admin from "@/pages/Admin";
import SessionExpiredModal from "@/components/SessionExpiredModal";
import SiteChrome from "@/components/SiteChrome";
import Blogs from "@/pages/Blogs";
import BlogPost from "@/pages/BlogPost";
import ClaimMaintain from "@/pages/ClaimMaintain";
import ClaimResult from "@/pages/ClaimResult";
import Contact from "@/pages/Contact";
import ForgotPassword from "@/pages/ForgotPassword";
import Home from "@/pages/Home";
import Feed from "@/pages/Feed";
import Login from "@/pages/Login";
import NewSkill from "@/pages/NewSkill";
import NotFound from "@/pages/NotFound";
import Notifications from "@/pages/Notifications";
import Privacy from "@/pages/Privacy";
import Register from "@/pages/Register";
import Settings from "@/pages/Settings";
import Profile from "@/pages/Profile"; // [profiles+follows]
import Skill from "@/pages/Skill";
import SkillFiles from "@/pages/SkillFiles";
import Skills from "@/pages/Skills";
import Suggest from "@/pages/Suggest";
import Terms from "@/pages/Terms";
import SuggestionDetail from "@/pages/SuggestionDetail";
import ThreadDetail from "@/pages/ThreadDetail";

// Reset scroll to the top on every route change — SPA navigation otherwise keeps the previous
// page's scroll position. Resets the window and (defensively) any scrolled main container.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    document.scrollingElement?.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Mirrors the former Next.js app/ route tree. Param names (:skillSlug, :blogSlug,
// :suggestionId) match the originals so each page's useParams() works unchanged.
export default function App() {
  return (
    <>
      <ScrollToTop />
      <SiteChrome>
        {/* Routes are grouped by area. React Router v6 matches by specificity (not source order),
            so within a group order is irrelevant — the splat file route still ranks below the
            specific /suggest, /suggestions, /discussions routes automatically. */}
        <Routes>
          {/* ── Browse & profiles (public) ─────────────────────────────── */}
          <Route path="/" element={<Home />} />
          <Route path="/home" element={<Feed />} />
          <Route path="/skills" element={<Skills />} />
          <Route path="/u/:username" element={<Profile />} /> {/* [profiles+follows] */}

          {/* ── Skill detail + in-skill flows ──────────────────────────── */}
          <Route path="/skill/:skillSlug" element={<Skill />} />
          <Route path="/skill/:skillSlug/suggest" element={<RequireAuth><Suggest /></RequireAuth>} />
          <Route path="/skill/:skillSlug/suggestions/:suggestionId" element={<RequireAuth><SuggestionDetail /></RequireAuth>} />
          <Route path="/skill/:skillSlug/discussions/:threadId" element={<ThreadDetail />} />
          {/* Splat = the file path being viewed (e.g. /skill/foo/scripts/build.py). */}
          <Route path="/skill/:skillSlug/*" element={<SkillFiles />} />
          <Route path="/skills/new" element={<RequireAuth><NewSkill /></RequireAuth>} />

          {/* ── Docs, contact & legal ──────────────────────────────────── */}
          <Route path="/docs" element={<Blogs />} />
          <Route path="/docs/:blogSlug" element={<BlogPost />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/legal/privacy" element={<Privacy />} />
          <Route path="/legal/terms" element={<Terms />} />

          {/* ── Creator claim flow ─────────────────────────────────────── */}
          <Route path="/claim/result" element={<ClaimResult />} />
          <Route path="/claim/maintain" element={<ClaimMaintain />} />

          {/* ── Account (auth-required) ────────────────────────────────── */}
          <Route path="/notifications" element={<RequireAuth><Notifications /></RequireAuth>} />
          <Route path="/settings" element={<RequireAuth><Settings /></RequireAuth>} />
          <Route path="/admin" element={<RequireAuth><Admin /></RequireAuth>} />

          {/* ── Auth pages (bare, no chrome) ───────────────────────────── */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          {/* ── Fallback ───────────────────────────────────────────────── */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </SiteChrome>
      <SessionExpiredModal />
      <HelpButton />
      <FeedbackButton />
    </>
  );
}
